import React, { Fragment, useRef } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useDrag, useDrop } from 'react-dnd';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';

import { classNames } from '../../components/helpers/functions';
import useModal from '../../hooks/useModal';

export const Block = ({
	id,
	index,
	moveCard,
	block: blockObj,
	serviceReport,
	onEdit,
	onDelete,
}) => {
	const ref = useRef(null);

	const { setOpen, setClose } = useModal('deleteModal');

	const [{ handlerId }, drop] = useDrop({
		accept: 'block',
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId(),
			};
		},
		hover(item, monitor) {
			if (!ref.current) {
				return;
			}
			const dragIndex = item.index;
			const hoverIndex = index;
			// Don't replace items with themselves
			if (dragIndex === hoverIndex) {
				return;
			}
			// Determine rectangle on screen
			const hoverBoundingRect = ref.current?.getBoundingClientRect();
			// Get vertical middle
			const hoverMiddleY =
				(hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
			// Determine mouse position
			const clientOffset = monitor.getClientOffset();
			// Get pixels to the top
			const hoverClientY = clientOffset.y - hoverBoundingRect.top;
			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%
			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}
			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}
			// Time to actually perform the action
			moveCard(dragIndex, hoverIndex);
			// Note: we're mutating the monitor item here!
			// Generally it's better to avoid mutations,
			// but it's good here for the sake of performance
			// to avoid expensive index searches.
			item.index = hoverIndex;
		},
	});
	const [{ isDragging }, drag] = useDrag({
		type: 'block',
		item: () => {
			return { id, index };
		},
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
	});
	const opacity = isDragging ? 0 : 1;
	drag(drop(ref));

	const blockId = id || blockObj;

	const block = serviceReport?.blocks?.find((o) => o.id === blockId);

	if (!block) {
		return null;
	}

	return (
		<tr>
			<td colSpan={2}>
				<div
					ref={ref}
					style={{ opacity }}
					className="w-full relative group"
					data-handler-id={handlerId}
				>
					{block?.type === 'repair' && (
						<div className="bg-red-50 py-4 px-6 rounded-md mb-4">
							<div class="font-medium text-gray-900">{block?.name}</div>
							<div
								className="ql-editor"
								dangerouslySetInnerHTML={{
									__html: block?.content,
								}}
							/>
							<div className="mt-4 px-4 text-xs leading-6"></div>
						</div>
					)}
					{block?.type === 'solution' && (
						<div className="bg-gray-50 py-4 px-6 rounded-md mb-4">
							<div class="font-medium text-gray-900">{block?.name}</div>
							<div
								className="ql-editor"
								dangerouslySetInnerHTML={{
									__html: block?.content,
								}}
							/>
							<div className="mt-4 px-4 text-xs leading-6"></div>
						</div>
					)}
					{block?.type === 'pm' && (
						<div className="bg-blue-50 py-4 px-6 rounded-md mb-4">
							<div class="font-medium text-gray-900">{block?.name}</div>
							<div
								className="ql-editor"
								dangerouslySetInnerHTML={{
									__html: block?.content,
								}}
							/>
							<div className="mt-4 px-4 text-xs leading-6"></div>
						</div>
					)}
					{block?.type === 'notes' && (
						<div className="bg-yellow-50 py-4 px-6 rounded-md mb-4">
							<div class="font-medium text-gray-900">{block?.name}</div>
							<div
								className="ql-editor"
								dangerouslySetInnerHTML={{
									__html: block?.content,
								}}
							/>
							<div className="mt-4 px-4 text-xs leading-6"></div>
						</div>
					)}
					{block?.type === 'parts' && (
						<div className="bg-gray-50 py-4 px-6 rounded-md mb-4">
							<div class="font-medium text-gray-900">{block?.name}</div>
							<div
								className="ql-editor"
								dangerouslySetInnerHTML={{
									__html: block?.content,
								}}
							/>
							<div className="mt-4 px-4 text-xs leading-6"></div>
						</div>
					)}

					<div className="absolute top-4 right-3 opacity-0 group-hover:opacity-100 transition ease-in-out">
						<Menu as="div" className="relative inline-block text-left ">
							<div>
								<Menu.Button className="flex items-center rounded-full hover:text-gray-800">
									<span className="sr-only">Open options</span>
									<EllipsisVerticalIcon className="w-5 h-5 text-gray-500" />
								</Menu.Button>
							</div>

							<Transition
								as={Fragment}
								enter="transition ease-out duration-100"
								enterFrom="transform opacity-0 scale-95"
								enterTo="transform opacity-100 scale-100"
								leave="transition ease-in duration-75"
								leaveFrom="transform opacity-100 scale-100"
								leaveTo="transform opacity-0 scale-95"
							>
								<Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
									<div className="py-1">
										<Menu.Item>
											{({ active }) => (
												<div
													className={classNames(
														active
															? 'bg-gray-100 text-gray-900'
															: 'text-gray-700',
														'block px-4 py-2 text-xs cursor-pointer'
													)}
													onClick={() => onEdit(block)}
												>
													Edit
												</div>
											)}
										</Menu.Item>
										<Menu.Item>
											{({ active }) => (
												<div
													onClick={() =>
														setOpen({
															title:
																'Are you sure you want to remove the content block?',
															confirmText: 'Delete Block',
															onConfirm: () =>
																onDelete(block, () => setClose()),
														})
													}
													className={classNames(
														'hover:bg-red-50 text-red-800 block px-4 py-2 text-xs cursor-pointer'
													)}
												>
													Delete
												</div>
											)}
										</Menu.Item>
									</div>
								</Menu.Items>
							</Transition>
						</Menu>
					</div>
				</div>
				<div className="flex justify-end -mt-3 text-xs"></div>
			</td>
		</tr>
	);
};
